import { useCallback } from 'react';

import { ensureDefined } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import type { BackError } from '../../../utils/back-errors.util';

import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';

import type { SubmitHandler } from '../types/submit-handler.type';
import type { RemoveMutation } from '../types/props.type';

import { useFindResolvableServiceUsageIndexById } from './useFindIndexById.hook';

interface Params {
  readonly directMutation?: RemoveMutation;
  readonly formContext?: string;
  readonly handleRemoveSuccess: () => void;
  readonly serviceUsageId?: Id | null;
  readonly serviceUsagesGroupId: Id;
  readonly serviceUserId: Id;
}

interface FormikArrayActions {
  readonly removeInForm: (index: number) => void;
}

export const useRemoveResolvableServiceUsageSubmit = ({
  directMutation,
  formContext,
  handleRemoveSuccess,
  serviceUsageId: pServiceUsageId,
  serviceUsagesGroupId,
  serviceUserId,
}: Readonly<Params>) => {
  const findIndexById = useFindResolvableServiceUsageIndexById({ formContext });

  const handleSubmitFactory = useCallback(
    ({ removeInForm }: Readonly<FormikArrayActions>): SubmitHandler =>
      ({ memos, formikHelpers: { setSubmitting }, setFormError }) => {
        const { serviceUsageId } = ensureDefined({
          serviceUsageId: pServiceUsageId,
        });

        setFormError(null);

        const onError = (error: unknown) => {
          setFormError(userFriendlyErrors(error as BackError));
          setSubmitting(false);
        };

        const onSuccess = () => {
          const index = findIndexById(serviceUsageId);

          if (index === -1) {
            setFormError(["La prestation n'existe pas"]);
            setSubmitting(false);
            return;
          }

          removeInForm(index);

          handleRemoveSuccess();
          setSubmitting(false);
        };

        if (directMutation) {
          directMutation(
            {
              serviceUsageId,
              serviceUsagesGroupId,
              serviceUserId,
              versionData: memoForm2ApiMapper(memos),
            },
            {
              onError,
              onSuccess,
            },
          );

          return undefined;
        }

        onSuccess();

        return undefined;
      },
    [
      directMutation,
      findIndexById,
      serviceUsagesGroupId,
      serviceUserId,
      handleRemoveSuccess,
      pServiceUsageId,
    ],
  );

  return { handleSubmitFactory };
};
